// TreasureBox.js — Treasure box collection shelf for NeevStar profile
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';

const css = `
@keyframes tb-pulse {
  0%,100% { box-shadow: 0 0 10px rgba(255,214,0,0.3), 0 2px 8px rgba(0,0,0,0.1); transform: scale(1); }
  50%      { box-shadow: 0 0 28px rgba(255,214,0,0.8), 0 4px 16px rgba(0,0,0,0.15); transform: scale(1.04); }
}
@keyframes tb-shake {
  0%,100% { transform: rotate(0deg); }
  15%      { transform: rotate(-10deg) scale(1.1); }
  30%      { transform: rotate(10deg) scale(1.12); }
  45%      { transform: rotate(-7deg) scale(1.08); }
  60%      { transform: rotate(7deg) scale(1.05); }
  80%      { transform: rotate(-3deg); }
}
@keyframes tb-open {
  0%   { transform: scale(0.5) rotate(-5deg); opacity: 0; }
  65%  { transform: scale(1.1) rotate(2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}
@keyframes tb-itemIn {
  0%   { transform: scale(0) translateY(10px); opacity: 0; }
  70%  { transform: scale(1.15) translateY(-3px); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}
@keyframes tb-coinRain {
  0%   { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(70px) rotate(540deg); opacity: 0; }
}
@keyframes tb-confetti {
  0%   { transform: translateY(0) rotate(0); opacity: 1; }
  100% { transform: translateY(60px) rotate(360deg); opacity: 0; }
}
.tb-box-unopened {
  animation: tb-pulse 2s ease-in-out infinite;
  cursor: pointer;
}
.tb-box-shaking { animation: tb-shake 0.65s ease-in-out; }
.tb-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.88);
  z-index: 9993;
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  font-family: 'Nunito', sans-serif;
}
.tb-modal {
  background: linear-gradient(160deg, #0D1B4B 0%, #1A0533 60%, #0A0A2E 100%);
  border: 2px solid rgba(255,214,0,0.5);
  border-radius: 28px;
  padding: 28px 24px 22px;
  max-width: 340px;
  width: 100%;
  text-align: center;
  animation: tb-open 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
  position: relative;
  overflow: hidden;
  font-family: 'Nunito', sans-serif;
}
.tb-reward-item {
  animation: tb-itemIn 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards;
  opacity: 0;
}
.tb-coin-rain { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.tb-coin { position: absolute; font-size: 18px; animation: tb-coinRain 0.9s ease-in forwards; }
.tb-confetti-p {
  position: absolute; border-radius: 2px;
  animation: tb-confetti 1s ease-in forwards;
}
`;

export const BOX_TYPES = {
  common:    { id: 'common',    emoji: '📦', name: 'Common Box',    color: '#00BCD4', dark: '#0097A7', coins: [10,15,20],  badgeChance: 0.2, trigger: 'every 10 correct answers' },
  rare:      { id: 'rare',      emoji: '🎁', name: 'Rare Box',      color: '#8B2BE2', dark: '#6B1FAF', coins: [25,35,50],  badgeChance: 0.4, trigger: 'every vertical completed' },
  epic:      { id: 'epic',      emoji: '💎', name: 'Epic Box',      color: '#FF6B00', dark: '#CC5500', coins: [50,75,100], badgeChance: 0.7, trigger: 'every certificate earned' },
  legendary: { id: 'legendary', emoji: '👑', name: 'Legendary Box', color: '#FFD600', dark: '#CCA800', coins: [150,200,300], badgeChance: 1.0, trigger: 'every 30-day streak' },
};

const BADGES = ['Star Collector','Quiz Hero','Science Explorer','Math Wizard','Word Ninja','Speed Learner','Night Owl','Early Bird','Streak Master','Curious Mind'];
const CONFETTI_COLORS = ['#FFD600','#FF2D55','#00C851','#0066FF','#8B2BE2','#FF9500','#00BCD4'];

function getStorageKey(userEmail) {
  return `ns_treasure_boxes_${userEmail}`;
}

function getLocalBoxes(userEmail) {
  try { return JSON.parse(localStorage.getItem(getStorageKey(userEmail)) || '[]'); }
  catch { return []; }
}

function saveLocal(userEmail, boxes) {
  localStorage.setItem(getStorageKey(userEmail), JSON.stringify(boxes));
}

function syncToSupabase(userEmail, boxes) {
  supabase.from('bm_child_profiles')
    .update({ treasure_boxes: boxes })
    .eq('parent_email', userEmail)
    .then(() => {}).catch(() => {});
}

// Load from Supabase and merge with localStorage (union by id)
export async function loadAndMergeBoxes(userEmail) {
  try {
    const { data } = await supabase.from('bm_child_profiles')
      .select('treasure_boxes').eq('parent_email', userEmail).single();
    const remote = Array.isArray(data?.treasure_boxes) ? data.treasure_boxes : [];
    const local = getLocalBoxes(userEmail);
    // Merge: remote is source of truth, add any local-only boxes not yet synced
    const remoteIds = new Set(remote.map(b => b.id));
    const merged = [...remote, ...local.filter(b => !remoteIds.has(b.id))];
    saveLocal(userEmail, merged);
    return merged;
  } catch {
    return getLocalBoxes(userEmail);
  }
}

export function getBoxes(userEmail) {
  return getLocalBoxes(userEmail);
}

export function addBox(userEmail, type) {
  const boxes = getLocalBoxes(userEmail);
  boxes.push({ id: Date.now(), type, earned: new Date().toISOString(), opened: false });
  saveLocal(userEmail, boxes);
  syncToSupabase(userEmail, boxes);
}

export function markBoxOpened(userEmail, boxId) {
  const boxes = getLocalBoxes(userEmail);
  const idx = boxes.findIndex(b => b.id === boxId);
  if (idx !== -1) {
    boxes[idx].opened = true;
    saveLocal(userEmail, boxes);
    syncToSupabase(userEmail, boxes);
  }
}

function randomCoins(boxType) {
  const arr = BOX_TYPES[boxType]?.coins || [10];
  return arr[Math.floor(Math.random() * arr.length)];
}

function randomBadge() {
  return BADGES[Math.floor(Math.random() * BADGES.length)];
}

function OpenModal({ box, onClose, onRewardClaimed }) {
  const [phase, setPhase] = useState('shaking'); // shaking | revealed
  const [coins] = useState(randomCoins(box.type));
  const [badge] = useState(Math.random() < (BOX_TYPES[box.type]?.badgeChance || 0) ? randomBadge() : null);
  const [surpriseMission] = useState(Math.random() < 0.15);
  const [coinRain, setCoinRain] = useState([]);
  const [confetti, setConfetti] = useState([]);

  const def = BOX_TYPES[box.type] || BOX_TYPES.common;

  useEffect(() => {
    // Shake then reveal
    setTimeout(() => {
      setPhase('revealed');
      setCoinRain(Array.from({ length: 12 }, (_, i) => ({
        id: i, left: `${5 + Math.random() * 90}%`, delay: `${Math.random() * 0.5}s`,
      })));
      setConfetti(Array.from({ length: 18 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        top: `${5 + Math.random() * 40}%`,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        delay: `${Math.random() * 0.4}s`,
        size: 6 + Math.random() * 7,
      })));
    }, 700);
  }, []);

  const handleClaim = () => {
    onRewardClaimed(coins, badge);
    onClose();
  };

  return (
    <div className="tb-overlay">
      <div className="tb-modal">
        {/* Coin rain */}
        <div className="tb-coin-rain">
          {coinRain.map(c => (
            <span key={c.id} className="tb-coin" style={{ left: c.left, top: '15%', animationDelay: c.delay }}>🪙</span>
          ))}
          {confetti.map(c => (
            <div key={c.id} className="tb-confetti-p"
              style={{ left: c.left, top: c.top, background: c.color, width: c.size, height: c.size, animationDelay: c.delay }} />
          ))}
        </div>

        <p style={{ fontSize: 11, fontWeight: 900, color: `${def.color}`, letterSpacing: '0.08em', margin: '0 0 8px' }}>
          {def.name.toUpperCase()}
        </p>

        {/* Box */}
        <div className={phase === 'shaking' ? 'tb-box-shaking' : ''}
          style={{ fontSize: 72, margin: '0 0 8px', display: 'inline-block' }}>
          {def.emoji}
        </div>

        {phase === 'revealed' && (
          <div>
            {/* Coins */}
            <div className="tb-reward-item" style={{ animationDelay: '0s', marginBottom: 6 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,214,0,0.15)', border: '1.5px solid rgba(255,214,0,0.4)',
                borderRadius: 14, padding: '8px 18px',
              }}>
                <span style={{ fontSize: 22 }}>🪙</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: '#FFD600' }}>+{coins} NeevCoins</span>
              </div>
            </div>

            {/* Badge */}
            {badge && (
              <div className="tb-reward-item" style={{ animationDelay: '0.2s', marginBottom: 6 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: `${def.color}22`, border: `1.5px solid ${def.color}55`,
                  borderRadius: 12, padding: '6px 16px',
                }}>
                  <span style={{ fontSize: 18 }}>🏅</span>
                  <span style={{ fontSize: 14, fontWeight: 900, color: def.color }}>{badge} Badge!</span>
                </div>
              </div>
            )}

            {/* Surprise Mission */}
            {surpriseMission && (
              <div className="tb-reward-item" style={{ animationDelay: '0.4s', marginBottom: 6 }}>
                <div style={{
                  background: 'rgba(0,200,81,0.12)', border: '1.5px solid rgba(0,200,81,0.35)',
                  borderRadius: 12, padding: '6px 16px', display: 'inline-block',
                }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: '#00C851' }}>🎯 Surprise Mission Unlocked!</span>
                </div>
              </div>
            )}

            <button onClick={handleClaim} style={{
              background: 'linear-gradient(135deg,#FFD600,#FF9500)',
              border: 'none', borderRadius: 14, padding: '13px 32px',
              fontSize: 16, fontWeight: 900, color: '#1A1A2E',
              cursor: 'pointer', fontFamily: "'Nunito',sans-serif",
              marginTop: 14, width: '100%',
              boxShadow: '0 6px 20px rgba(255,150,0,0.4)',
            }}>
              ✅ Claim Reward!
            </button>
          </div>
        )}

        {phase === 'shaking' && (
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', fontWeight: 700, margin: '8px 0 0' }}>
            Opening...
          </p>
        )}
      </div>
    </div>
  );
}

export default function TreasureBoxShelf({ profile, onCoinsEarned }) {
  const [boxes, setBoxes] = useState([]);
  const [openingBox, setOpeningBox] = useState(null);

  useEffect(() => {
    if (!document.getElementById('tb-css')) {
      const s = document.createElement('style');
      s.id = 'tb-css';
      s.textContent = css;
      document.head.appendChild(s);
    }
    if (profile?.parent_email) {
      // Load from Supabase and merge with localStorage so boxes survive device switches
      loadAndMergeBoxes(profile.parent_email).then(merged => setBoxes(merged));
    }
  }, [profile?.parent_email]);

  const unopened = boxes.filter(b => !b.opened);
  const opened = boxes.filter(b => b.opened);

  const handleOpen = (box) => setOpeningBox(box);

  const handleRewardClaimed = (coins, badge) => {
    markBoxOpened(profile.parent_email, openingBox.id);
    setBoxes(getBoxes(profile.parent_email));
    onCoinsEarned && onCoinsEarned(coins);
    setOpeningBox(null);
  };

  if (boxes.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ background: 'white', borderRadius: 20, padding: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <p style={{ fontSize: 14, fontWeight: 900, color: '#1A1A2E', margin: 0 }}>
            📦 Treasure Boxes
          </p>
          <span style={{
            background: '#FFF3E0', color: '#FF9500',
            borderRadius: 8, padding: '2px 8px', fontSize: 11, fontWeight: 900,
          }}>
            {unopened.length} unopened
          </span>
        </div>

        {/* Unopened boxes */}
        {unopened.length > 0 && (
          <>
            <p style={{ fontSize: 11, fontWeight: 800, color: '#888', letterSpacing: '0.06em', margin: '0 0 8px' }}>
              TAP TO OPEN
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              {unopened.map(box => {
                const def = BOX_TYPES[box.type] || BOX_TYPES.common;
                return (
                  <div
                    key={box.id}
                    className="tb-box-unopened"
                    onClick={() => handleOpen(box)}
                    style={{
                      width: 68, height: 68,
                      background: `linear-gradient(135deg, ${def.color}22, ${def.dark}33)`,
                      border: `2px solid ${def.color}66`,
                      borderRadius: 16,
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <span style={{ fontSize: 30 }}>{def.emoji}</span>
                    <span style={{ fontSize: 9, fontWeight: 900, color: def.color }}>{def.name.split(' ')[0]}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Opened boxes */}
        {opened.length > 0 && (
          <>
            <p style={{ fontSize: 11, fontWeight: 800, color: '#CCC', letterSpacing: '0.06em', margin: '0 0 8px' }}>
              OPENED ({opened.length})
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {opened.slice(-8).map(box => {
                const def = BOX_TYPES[box.type] || BOX_TYPES.common;
                return (
                  <div key={box.id} style={{
                    width: 44, height: 44,
                    background: '#F5F5F5', border: '1.5px solid #E0E0E0',
                    borderRadius: 12,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, opacity: 0.5,
                  }}>
                    {def.emoji}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* How to earn more */}
        <div style={{ marginTop: 12, background: '#F8F9FF', borderRadius: 12, padding: '10px 12px' }}>
          <p style={{ fontSize: 11, fontWeight: 800, color: '#888', margin: '0 0 4px' }}>HOW TO EARN MORE BOXES</p>
          {Object.values(BOX_TYPES).map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: 14 }}>{t.emoji}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#666' }}>{t.name} — {t.trigger}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Open animation modal */}
      {openingBox && (
        <OpenModal
          box={openingBox}
          onClose={() => setOpeningBox(null)}
          onRewardClaimed={handleRewardClaimed}
        />
      )}
    </div>
  );
}
